import { useUserRole } from './useUserRole';
import type { CaregiverRole } from '@/types';

export interface Permissions {
  accessLevel: CaregiverRole | null;
  resolved: boolean;
  canView: boolean;
  canEdit: boolean;
  canComplete: boolean;
  canDelete: boolean;
  canManageMembers: boolean;
  canReview: boolean;
  canEditChildren: boolean;
  /**
   * Owner-only (PRIMARY). Gates the household's "ownership" surface —
   * billing/plan, Gmail connection + approved senders, deleting the
   * household, and transferring ownership. A Co-Admin manages the
   * household but never owns it, so these stay exactly-one-owner.
   */
  isOwner: boolean;
}

/**
 * Single client-side permission check. UX only — the source of truth is
 * Postgres RLS (see the access-level migrations + access_levels_rls.test.sql).
 * Mirrors the four-level capability matrix:
 *   PRIMARY   (Admin)    -> everything, incl. owner-only surfaces
 *   CO_ADMIN  (Co-Admin) -> everything EXCEPT owner-only (billing, Gmail,
 *                           delete/transfer household)
 *   EDITOR    (Can edit) -> view + add/edit/complete content
 *   VIEWER (Can view) -> view only
 */
export function usePermissions(): Permissions {
  const { role, roleResolved } = useUserRole();
  const isOwner = role === 'PRIMARY';
  const isManager = role === 'PRIMARY' || role === 'CO_ADMIN';
  const isWriter = isManager || role === 'EDITOR';
  return {
    accessLevel: role,
    resolved: roleResolved,
    canView: role != null,
    canEdit: isWriter,
    canComplete: isWriter,
    canDelete: isManager,
    canManageMembers: isManager,
    canReview: isManager,
    canEditChildren: isManager,
    isOwner,
  };
}
